import { axios } from '@/lib/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

export const getECCNs = async (query) => {
  const { page, pageSize, sortBy = '', orderBy = 1, filters = {} } = query

  // Transform array filters to comma-separated strings for backend
  const transformedFilters = { ...filters }
  Object.keys(transformedFilters).forEach((key) => {
    if (Array.isArray(transformedFilters[key])) {
      transformedFilters[key] = transformedFilters[key].join(',')
    }
  })

  const params = {
    page,
    limit: pageSize,
    sortBy,
    orderBy,
    ...transformedFilters,
  }
  const response = await axios.get('/ECCNs', { params })
  return response.data
}

export const createECCN = async (data) => {
  const response = await axios.post('/ECCNs', data)
  return response.data
}

export const updateECCN = async ({ id, data }) => {
  const response = await axios.put(`/ECCNs/${id}`, data)
  return response.data
}

export const deleteECCN = async (id) => {
  const response = await axios.delete(`/ECCNs/${id}`)
  return response.data
}

export const uploadECCNs = async (file) => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post('/ECCNs/upload', formData)
  return response.data
}

const useECCN = () => {
  const queryClient = useQueryClient()

  const useFetchAllECCNs = (query) => {
    return useQuery({
      queryKey: ['eccns', query],
      queryFn: () => getECCNs(query),
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to fetch ECCNs')
      },
    })
  }

  const useCreateECCN = () => {
    return useMutation({
      mutationFn: createECCN,
      onSuccess: () => {
        queryClient.invalidateQueries(['eccns'])
        toast.success('ECCN created successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to create ECCN')
      },
    })
  }

  const useUpdateECCN = () => {
    return useMutation({
      mutationFn: updateECCN,
      onSuccess: () => {
        queryClient.invalidateQueries(['eccns'])
        toast.success('ECCN updated successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update ECCN')
      },
    })
  }

  const useRemoveECCN = () => {
    return useMutation({
      mutationFn: deleteECCN,
      onSuccess: () => {
        queryClient.invalidateQueries(['eccns'])
        toast.success('ECCN deleted successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to delete ECCN')
      },
    })
  }

  const useUploadECCNs = () => {
    return useMutation({
      mutationFn: uploadECCNs,
      onSuccess: () => {
        queryClient.invalidateQueries(['eccns'])
        toast.success('ECCNs uploaded successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to upload ECCNs')
      },
    })
  }

  return {
    useFetchAllECCNs,
    useCreateECCN,
    useUpdateECCN,
    useRemoveECCN,
    useUploadECCNs,
  }
}

export default useECCN
